<template>
  <div v-if="contactInfoSaved">
    <p>{{ contact.first_name }} {{ contact.last_name }}</p>
    <p>{{ contact.email }}</p>
    <p>{{ contact.phone }}</p>
    <button
      id="contact-info-summary"
      class="text-button-guest-primary underline mt-2 inline-block"
      @click="backToFormView"
    >
      Edit
    </button>
  </div>
  <form v-else @submit.prevent class="mb-8">
    <div class="flex flex-col space-y-2 xs:flex-row xs:space-y-0">
      <TextInput
        id="fname"
        forInput="fname-input"
        v-model="contact.first_name"
        label="First Name"
        classes="w-full xs:w-1/2 xs:pr-2"
        placeholder="First Name"
      />
      <TextInput
        id="lname"
        forInput="lname-input"
        v-model="contact.last_name"
        label="Last Name"
        classes="w-full xs:w-1/2"
        placeholder="Last Name"
      />
    </div>
    <div class="flex flex-col space-y-2 mt-4 xs:flex-row xs:space-y-0 mb-8">
      <div class="w-full xs:w-3/5 xs:pr-2">
        <TextInput
          id="email"
          forInput="email-input"
          type="email"
          v-model="contact.email"
          label="Email"
          placeholder="Email"
          :inputClasses="`transition duration-200 ease-in-out ${
            emailError ? '!border-[#df1b41] !text-[#df1b41]' : 'border-transparent'
          }`"
          @blur="handleEmailBlur"
          @update:modelValue="handleEmailUpdate"
        />
        <p v-if="emailError" class="text-[#df1b41] text-sm mt-0.5" id="email-error-message">
          {{ emailError }}
        </p>
      </div>
      <div class="w-full xs:w-2/5">
        <TextInput
          id="tel"
          forInput="tel-input"
          type="tel"
          v-model="contact.phone"
          label="Phone"
          placeholder="Phone"
          classes="w-full"
          :inputClasses="`transition duration-200 ease-in-out ${
            phoneError ? '!border-[#df1b41] !text-[#df1b41]' : 'border-transparent'
          }`"
          @blur="handlePhoneBlur"
          @update:modelValue="handlePhoneUpdate"
        />
        <p v-if="phoneError" class="text-[#df1b41] text-sm mt-0.5" id="phone-error-message">
          {{ phoneError }}
        </p>
      </div>
    </div>

    <p
      v-if="error.message"
      id="error-display"
      class="p-2 mt-4 rounded border border-red-400 bg-red-200 text-red-600"
    >
      {{ error.message }}
    </p>

    <button
      v-if="requiresPayment"
      id="enter-payment-button"
      class="w-full md:w-1/2 px-4 py-2 uppercase border border-solid rounded font-semibold bg-button-guest-primary font-button text-button-label border-button-guest-primary disabled:bg-disabled disabled:cursor-not-allowed disabled:border-disabled disabled:text-white"
      :disabled="!isContactFormValid || isSaving"
      @click="saveContactAndShowPayment"
    >
      {{ enterPaymentButtonText }}
    </button>
  </form>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, ref } from 'vue'
import { z } from 'zod'

import TextInput from '@/common/components/molecules/TextInput.vue'
import { useCheckoutStore } from '@/stores/checkout'
import { ErrorData, blankError, clearError, displayHandled } from '@/utils/errors'

const props = defineProps<{
  mountStripeForm?: () => void
  unmountStripeForm?: () => void
}>()

const checkoutStore = useCheckoutStore()
const { contact, requiresPayment, hasSufficientGuestInfo } = storeToRefs(checkoutStore)

// Email validation
const emailSchema = z.string().email({ message: 'Please enter a valid email address.' })
const emailError = ref<string>('')
const isEmailValid = computed<boolean>(() => emailError.value === '')
function validateEmail(email: string): void {
  const result = emailSchema.safeParse(email)
  if (!result.success) emailError.value = result.error.issues[0].message
  else emailError.value = ''
}
onMounted(handleEmailBlur)
function handleEmailBlur(): void {
  if (contact.value.email.length > 0) validateEmail(contact.value.email)
  else emailError.value = ''
}
function handleEmailUpdate(email: string): void {
  if (email.length === 0) emailError.value = ''
}

// Phone validation
const phoneSchema = z
  .string()
  .max(20, { message: 'Please ensure this field has no more than 20 characters.' })
const phoneError = ref<string>('')
const isPhoneValid = computed<boolean>(() => phoneError.value === '')
function validatePhone(phone: string): void {
  const result = phoneSchema.safeParse(phone)
  if (!result.success) phoneError.value = result.error.issues[0].message
  else phoneError.value = ''
}
onMounted(handlePhoneBlur)
function handlePhoneBlur(): void {
  if (contact.value.phone.length > 0) validatePhone(contact.value.phone)
  else phoneError.value = ''
}
function handlePhoneUpdate(phone: string): void {
  if (phone.length === 0) phoneError.value = ''
}

// Form state
const contactInfoSaved = ref<boolean>(false)
function backToFormView(): void {
  contactInfoSaved.value = false
  props.unmountStripeForm?.()
}
const isSaving = ref<boolean>(false)
const error = ref<ErrorData>(blankError())
const isContactFormValid = computed<boolean>(
  () => hasSufficientGuestInfo.value && isEmailValid.value && isPhoneValid.value,
)

// Form UI
const enterPaymentButtonText = computed<string>(() => {
  return isSaving.value ? 'LOADING...' : 'ENTER PAYMENT'
})

// Form submission
async function saveContactAndShowPayment(): Promise<void> {
  if (!isContactFormValid.value || isSaving.value) return
  isSaving.value = true
  clearError(error.value)
  try {
    await checkoutStore.setContactInfo()
    contactInfoSaved.value = true
    await nextTick(props.mountStripeForm)
  } catch (e) {
    displayHandled(e, error.value)
  } finally {
    isSaving.value = false
  }
}

defineExpose({ isContactFormValid, contactInfoSaved })
</script>
